import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SITE_CONFIG } from "../data/config";
import { publicApi, formatApiError } from "../lib/api";
import {
  Lightbulb,
  Send,
  MessageCircle,
  CheckCircle2,
  AlertCircle,
  Eye,
  ArrowLeft,
  Loader2,
} from "lucide-react";

const MIN_LEN = 10;
const MAX_LEN = 800;

const FeedbackSection = () => {
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState(null); // null | "success" | "error"
  const [errorMsg, setErrorMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [publicCount, setPublicCount] = useState(0);

  useEffect(() => {
    publicApi
      .get("/suggestions/count")
      .then((res) => setPublicCount(res.data?.total || 0))
      .catch(() => setPublicCount(0));
  }, []);

  const trimmed = message.trim();
  const isTooShort = trimmed.length > 0 && trimmed.length < MIN_LEN;
  const canSubmit = trimmed.length >= MIN_LEN && !submitting;

  const resetForm = () => {
    setMessage("");
    setName("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      if (trimmed.length < MIN_LEN) {
        setStatus("error");
        setErrorMsg(`النص قصير جدًا (${MIN_LEN} أحرف على الأقل).`);
      }
      return;
    }
    setSubmitting(true);
    setStatus(null);
    setErrorMsg("");
    try {
      await publicApi.post("/suggestions", {
        name: name.trim() || null,
        content: trimmed,
      });
      setStatus("success");
      resetForm();
    } catch (err) {
      setStatus("error");
      setErrorMsg(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const shareViaWhatsapp = () => {
    const lines = [
      "مرحباً Rush Store 👋",
      "لدي اقتراح / ملاحظة أرغب بمشاركته:",
      "",
      name.trim() ? `👤 الاسم: ${name.trim()}` : "👤 الاسم: (بدون اسم)",
      "",
      "─── الرسالة ───",
      trimmed || "(بدون نص)",
    ];
    const url = `https://wa.me/${
      SITE_CONFIG.whatsappNumber
    }?text=${encodeURIComponent(lines.join("\n"))}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <section
      id="feedback"
      data-testid="feedback-section"
      className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16"
    >
      <div className="rounded-3xl bg-gradient-to-br from-white/[0.05] to-white/[0.01] border border-[#C9A96A]/20 backdrop-blur-xl shadow-2xl shadow-blue-900/20 overflow-hidden">
        {/* Header */}
        <div className="flex items-start gap-3 px-5 sm:px-8 pt-6 pb-4 border-b border-white/5 bg-[#C9A96A]/[0.04]">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-b from-[#D4B278] to-[#B08A4A] text-[#0A1128] flex items-center justify-center shadow-lg flex-shrink-0">
            <Lightbulb className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#C9A96A]/[0.08] border border-[#C9A96A]/25 text-[#D4B98A] text-[10px] font-bold mb-1.5">
              رأيك يهمّنا
            </div>
            <h2 className="text-white font-black text-xl sm:text-2xl leading-tight">
              المقترحات والملاحظات
            </h2>
            <p className="text-white/60 text-xs sm:text-sm mt-1 leading-relaxed">
              شاركنا اقتراحاتك أو ملاحظاتك لنحسّن تجربة Rush Store — نراجع كل
              رسالة قبل نشرها.
            </p>
          </div>
        </div>

        {/* Public suggestions link */}
        <div className="px-5 sm:px-8 pt-5">
          <Link
            to="/suggestions"
            data-testid="view-suggestions-link"
            className="flex items-center justify-between gap-3 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 hover:border-[#C9A96A]/40 p-3 sm:p-3.5 transition-colors group"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-[#C9A96A]/10 border border-[#C9A96A]/30 flex items-center justify-center flex-shrink-0">
                <Eye className="w-5 h-5 text-[#E5CB94]" />
              </div>
              <div className="min-w-0">
                <div className="text-white font-bold text-sm">
                  عرض الاقتراحات المنشورة
                </div>
                <div
                  data-testid="public-count"
                  className="text-white/50 text-[11px] mt-0.5"
                >
                  <span className="text-[#E5CB94] font-bold tabular-nums">
                    {publicCount}
                  </span>{" "}
                  اقتراحًا منشورًا
                </div>
              </div>
            </div>
            <ArrowLeft className="w-4 h-4 text-white/40 group-hover:text-[#E5CB94] transition-colors flex-shrink-0" />
          </Link>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 sm:p-8 space-y-5">
          <div>
            <label
              htmlFor="fb-name"
              className="block text-white/80 text-xs font-bold mb-2"
            >
              الاسم <span className="text-white/40 font-medium">(اختياري)</span>
            </label>
            <input
              id="fb-name"
              type="text"
              data-testid="feedback-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={60}
              placeholder="مثلاً: أحمد"
              className="w-full h-12 rounded-xl bg-white/[0.03] border border-white/10 text-white px-4 text-sm placeholder:text-white/30 focus:outline-none focus:border-[#C9A96A]/50 focus:bg-white/[0.05] transition-colors"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label
                htmlFor="fb-message"
                className="block text-white/80 text-xs font-bold"
              >
                اقتراحك أو ملاحظتك
              </label>
              <span
                className={`text-[10px] tabular-nums ${
                  trimmed.length > MAX_LEN * 0.9
                    ? "text-orange-300"
                    : "text-white/40"
                }`}
              >
                {trimmed.length} / {MAX_LEN}
              </span>
            </div>
            <textarea
              id="fb-message"
              data-testid="feedback-message-input"
              value={message}
              onChange={(e) => {
                setMessage(e.target.value.slice(0, MAX_LEN));
                if (status) {
                  setStatus(null);
                  setErrorMsg("");
                }
              }}
              rows={5}
              placeholder="اكتب اقتراحك بوضوح — لا نطلب رقم جوال أو بريد إلكتروني."
              className={`w-full rounded-xl bg-white/[0.03] border text-white px-4 py-3 text-sm placeholder:text-white/30 focus:outline-none focus:bg-white/[0.05] transition-colors resize-y min-h-[120px] ${
                isTooShort
                  ? "border-orange-500/50 focus:border-orange-500"
                  : "border-white/10 focus:border-[#C9A96A]/50"
              }`}
            />
            {isTooShort && (
              <p className="text-orange-300 text-[11px] mt-1.5 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                يجب أن تكون الرسالة على الأقل {MIN_LEN} أحرف.
              </p>
            )}
          </div>

          {status === "success" && (
            <div
              data-testid="feedback-success"
              className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-3.5 text-emerald-200 text-sm flex flex-col gap-3"
            >
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold">تم استلام اقتراحك بنجاح</div>
                  <div className="text-emerald-200/80 text-xs mt-0.5">
                    سيتم مراجعته قبل نشره. شكرًا لمشاركتك!
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={shareViaWhatsapp}
                className="self-start inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 text-emerald-100 text-xs font-bold transition"
              >
                <MessageCircle className="w-3.5 h-3.5" />
                إرسال نسخة عبر واتساب (اختياري)
              </button>
            </div>
          )}

          {status === "error" && (
            <div
              data-testid="feedback-error"
              className="flex items-start gap-2 rounded-xl bg-red-500/10 border border-red-500/30 p-3 text-red-200 text-sm"
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-bold">تعذّر الإرسال</div>
                <div className="text-red-200/80 text-xs mt-0.5">
                  {errorMsg || `تأكد من كتابة الاقتراح بشكل واضح (على الأقل ${MIN_LEN} أحرف) ثم حاول مرة أخرى.`}
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 pt-1">
            <button
              type="submit"
              data-testid="feedback-submit-button"
              disabled={!canSubmit}
              className="flex-1 inline-flex items-center justify-center gap-2 h-12 px-5 rounded-xl bg-gradient-to-b from-[#D4B278] to-[#B08A4A] text-[#0A1128] font-black text-sm hover:brightness-110 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  جارٍ الإرسال...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  إرسال الاقتراح
                </>
              )}
            </button>
            <a
              href={`https://wa.me/${SITE_CONFIG.whatsappNumber}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 h-12 px-5 rounded-xl bg-white/[0.03] border border-white/10 text-white hover:border-emerald-500/40 hover:text-emerald-200 font-bold text-sm transition"
            >
              <MessageCircle className="w-4 h-4" />
              محادثة مباشرة
            </a>
          </div>

          <p className="text-white/40 text-[11px] leading-relaxed text-center">
            لا نطلب أي بيانات حساسة. نستخدم اسم اختياري فقط، ومحتوى الرسالة يمر
            بمراجعة قبل النشر.
          </p>
        </form>
      </div>
    </section>
  );
};

export default FeedbackSection;
